import { FC, useCallback, useState } from 'react';
import { HierarchyList, InputWithSearchIcon } from 'common/components';
import SearchList from 'common/components/custom/search-list/SearchList';
import SearchSelection from 'common/components/custom/search-list/SearchSelection';
import { EntityVM, MasterDataEntity } from 'common/models/data/entity.model';
import { getHierarchyListColDefs } from 'modules/master-data/config';

import styles from './SingleSelectMasterData.module.scss';
import { ApiStatus, EntityType } from 'common/enums';
import { SearchDTO } from 'common/models';
import useDebounce from 'common/hooks/useDebounce';
import { SEARCH_DEBOUNCE_DELAY } from 'common/config';
import { useDispatch, useSelector } from 'react-redux';
import { masterDataActions } from 'modules/master-data/redux';
import { RootState } from 'common/redux/core/root.reducer';
import {
    getSearchList,
    getSearchListPagination,
    getSearchListStatus
} from 'modules/master-data/redux/masterData.selector';
import { NO_RECORDS_FOUND } from 'common/config/search.config';
import { removeByEntityType } from 'common/utils/table';
import InfiniteScroll from 'common/components/custom/infinite-scroll/InfiniteScroll';

type SingleSelectMasterDataProps = {
    hierarchicalData: MasterDataEntity[];
    toggleExpansion: (payload: EntityVM) => void;
    updateSelectedRadioButton: (entity: EntityVM) => void;
    hideChildIndicatorsOnly: boolean;
    hideSelectionOnCategory: boolean;
    disbleEntity?: EntityVM;
    preventSelectionOfSubCategoriesHavingSubCategories?: boolean;
    preventSelectionOfSubCategoriesHavingIndicators?: boolean;
    fetchNextHierachySet: () => void;
    radioListClassName?: string;
};

const SingleSelectMasterData: FC<SingleSelectMasterDataProps> = ({
    hierarchicalData,
    toggleExpansion,
    updateSelectedRadioButton,
    hideChildIndicatorsOnly,
    hideSelectionOnCategory = false,
    disbleEntity,
    preventSelectionOfSubCategoriesHavingSubCategories,
    preventSelectionOfSubCategoriesHavingIndicators,
    fetchNextHierachySet,
    radioListClassName
}) => {
    const dispatch = useDispatch();
    const [searchVal, setSearchVal] = useState<string>('');
    const [hideSearch, setHideSearch] = useState<boolean>(false);
    const [pageNumber, setPageNumber] = useState<number>(1);

    const [radioButtonForSearch, setRadioButtonForSearch] = useState<string>('');
    const [selectedRecord, setSelectedRecord] = useState<any>();

    const searchList = useSelector((state: RootState) => getSearchList(state));
    const searchListPagination = useSelector((state: RootState) => getSearchListPagination(state));
    const searchListStatus = useSelector((state: RootState) => getSearchListStatus(state));

    const getSearchApiPayload = useCallback(() => {
        const filter: string[] = [
            EntityType.Category,
            EntityType.SubCategory,
            EntityType.Indicator
        ];
        const apiPayload: SearchDTO = {
            searchText: searchVal,
            filter: filter,
            pageNumber: 1,
            pageSize: 20,
            loadPath: true
        };

        return apiPayload;
    }, [searchVal]);

    const onSearchChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchVal(event.target.value);
        if (event.target.value.length < 3) dispatch(masterDataActions.resetSearchResult());
    };

    useDebounce(
        () => {
            if (searchVal.length >= 3)
                dispatch(masterDataActions.searchList(getSearchApiPayload()));
        },
        [searchVal],
        SEARCH_DEBOUNCE_DELAY
    );

    const handleChangeForSearch = (event: React.ChangeEvent<HTMLInputElement>, list: any) => {
        setRadioButtonForSearch(event.target.value);
        setHideSearch(true);
        setSelectedRecord(list);
        handleRadioClicked(list);
    };

    const handleRadioClicked = (data: EntityVM) => {
        updateSelectedRadioButton(data);
    };

    const clearSelection = () => {
        setSearchVal('');
        setHideSearch(false);
        setRadioButtonForSearch('');
    };

    const fetchNextSearchList = () => {
        if (
            searchListPagination &&
            searchListPagination.totalPages > searchListPagination.currentPage
        ) {
            const apiPayload = getSearchApiPayload();
            apiPayload.pageNumber = pageNumber + 1;
            dispatch(masterDataActions.searchList(apiPayload));
            setPageNumber(pageNumber + 1);
        }
    };

    const searchResult =
        searchList.length === 0 ? (
            <center>
                <b className="search-results">{NO_RECORDS_FOUND}</b>
            </center>
        ) : (
            <>
                {searchList.map((list: any, index: number) => {
                    return (
                        <SearchList
                            radioButtonForSearch={radioButtonForSearch}
                            handleChangeForSearch={handleChangeForSearch}
                            key={index}
                            searchVal={searchVal}
                            data={list}
                            disbleEntity={disbleEntity}
                            hideSelectionOnCategory={hideSelectionOnCategory}
                            preventSelectionOfSubCategoriesHavingSubCategories={
                                preventSelectionOfSubCategoriesHavingSubCategories
                            }
                            preventSelectionOfSubCategoriesHavingIndicators={
                                preventSelectionOfSubCategoriesHavingIndicators
                            }
                        />
                    );
                })}
                <InfiniteScroll
                    targetId="hierarchy-search-list"
                    isDataLoading={searchListStatus === ApiStatus.LOADING}
                    currentRecordCount={
                        searchListPagination &&
                        searchListPagination.currentPage * searchListPagination.pageSize
                    }
                    totalRecordCount={searchListPagination ? searchListPagination.totalCount : 0}
                    fetchNext={fetchNextSearchList}
                />
            </>
        );

    return (
        <>
            {hideSearch === false && (
                <InputWithSearchIcon
                    placeholder="Search"
                    value={searchVal}
                    onChange={onSearchChangeHandler}
                    hideClearButton={false}></InputWithSearchIcon>
            )}
            {searchVal.length >= 3 ? (
                <div className={styles.searchListWrapper} id="hierarchy-search-list">
                    {hideSearch === false ? (
                        searchResult
                    ) : (
                        <SearchSelection clearSelection={clearSelection} data={selectedRecord} />
                    )}
                </div>
            ) : (
                <HierarchyList
                    isNoDataVisible={false}
                    data={removeByEntityType(hierarchicalData, EntityType.Indicator)}
                    columnDefs={getHierarchyListColDefs(
                        handleRadioClicked,
                        toggleExpansion,
                        450,
                        hideChildIndicatorsOnly,
                        hideSelectionOnCategory,
                        disbleEntity,
                        preventSelectionOfSubCategoriesHavingSubCategories,
                        preventSelectionOfSubCategoriesHavingIndicators
                    )}
                    handleScroll={fetchNextHierachySet}
                    className={radioListClassName}
                />
            )}
        </>
    );
};

export default SingleSelectMasterData;
